import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  Users,
  Briefcase,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Sparkles,
} from "lucide-react";

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Services",
      links: [
        { label: "IT Solutions", path: "/services" },
        { label: "Brand Development", path: "/services" },
        { label: "IT Procurement", path: "/instant-it-quote" },
        { label: "Digital Transformation", path: "/services" },
        { label: "24/7 Support", path: "/services" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About Us", path: "/about" },
        { label: "Carrers", path: "/growth" },
        { label: "Partnerships", path: "/growth" },
        { label: "Investment", path: "/growth" },
      ],
    },
  ];

  const contactInfo = [
    { icon: Phone, label: "+91 73046 60050" },
    { icon: Mail, label: "Sales@oratorhype.com" },
    { icon: MapPin, label: "India • Global Presence" },
  ];

  const socialLinks = [
    {
      icon: Linkedin,
      url: "https://www.linkedin.com/company/orator-hype/",
      label: "LinkedIn",
    },
    // { icon: Twitter, url: "https://twitter.com", label: "Twitter" },
    {
      icon: Facebook,
      url: "https://www.facebook.com/people/Orator-Hype/61570113037117/#",
      label: "Facebook",
    },
    {
      icon: Instagram,
      url: "https://www.instagram.com/orator_mumbai/",
      label: "Instagram",
    },
  ];

  const stats = [
    { icon: Award, value: "13+", label: "Years Experience" },
    { icon: Users, value: "196+", label: "Happy Clients" },
    { icon: Briefcase, value: "223+", label: "Projects" },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSocialClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <footer className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-gray-900 to-black pt-16 pb-8">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-blue-500/5 backdrop-blur-sm rounded-full animate-float-slow"></div>
        <div className="absolute top-32 right-20 w-20 h-20 bg-purple-500/5 backdrop-blur-sm rounded-full animate-float delay-1000"></div>
        <div className="absolute bottom-10 right-1/4 w-16 h-16 bg-cyan-500/5 backdrop-blur-sm rounded-full animate-float-slow delay-1500"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-4 mb-6"
            >
              <div className="relative">
                <div className="w-48 h-16 rounded-2xl   flex items-center justify-center">
                  <img
                    src="/assets/logo.png"
                    alt="Orator Hype"
                    className="w-30 h-30 object-contain"
                  />
                </div>
              </div>
              <div></div>
            </motion.div>

            <p className="text-sm text-gray-400">
              Transforming Businesses Digitally
            </p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-gray-300 text-sm leading-relaxed max-w-md mb-8"
            >
              Leading Indian enterprise delivering scalable SaaS, IT
              Infrastructure, Cybersecurity, Healthcare Technology & Digital
              Branding solutions globally.
            </motion.p>

            {/* Contact Info */}
            <div className="space-y-3 mb-8">
              {contactInfo.map((contact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex items-center gap-3 text-gray-300 text-sm"
                >
                  <contact.icon className="w-4 h-4 text-cyan-400" />
                  <span>{contact.label}</span>
                </motion.div>
              ))}
            </div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex items-center gap-3"
            >
              {socialLinks.map((social, index) => (
                <motion.button
                  key={social.label}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSocialClick(social.url)}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all duration-300 group"
                >
                  <social.icon className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" />
                </motion.button>
              ))}
            </motion.div>
          </div>

          {/* Links Sections */}
          {footerSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 + sectionIndex * 0.1 }}
            >
              <h3 className="text-sm font-semibold text-white mb-5 tracking-wider flex items-center gap-2">
                <Sparkles className="w-3 h-3 text-cyan-400" />
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <motion.li
                    key={link.label}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: 0.3 + sectionIndex * 0.1 + linkIndex * 0.05,
                    }}
                  >
                    <button
                      onClick={() => handleNavigation(link.path)}
                      className="text-sm text-gray-400 hover:text-white transition-colors duration-300 flex items-center gap-2 group"
                    >
                      <div className="w-1 h-1 bg-cyan-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      {link.label}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-3 gap-6 mb-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="text-2xl md:text-3xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-xs text-gray-400 flex items-center justify-center gap-2">
                  <stat.icon className="w-4 h-4 text-cyan-400" />
                  {stat.label}
                </div>
                <div className="w-0 group-hover:w-16 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent transition-all duration-500 mt-2 mx-auto"></div>
              </motion.div>
            ))}
          </motion.div>

          {/* Legal Links */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
            <div className="flex items-center gap-6">
              {["Privacy Policy", "Terms of Service", "Cookie Policy"].map(
                (item, index) => (
                  <motion.button
                    key={item}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    onClick={() => handleNavigation("/")}
                    className="text-xs text-gray-500 hover:text-white transition-colors duration-300"
                  >
                    {item}
                  </motion.button>
                ),
              )}
            </div>

            {/* Newsletter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 text-xs text-gray-500"
            >
              <span>Stay updated with our latest insights</span>
              <div className="w-1 h-1 bg-cyan-400 rounded-full"></div>
            </motion.div>
          </div>

          {/* Copyright */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-xs text-gray-600 pt-6 border-t border-white/5"
          >
            © {currentYear} Orator Hype. All rights reserved.
            <span className="mx-2">•</span>
            Transforming digital landscapes since 2011
          </motion.div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        
        .delay-1000 { animation-delay: 1s; }
        .delay-1500 { animation-delay: 1.5s; }
      `}</style>
    </footer>
  );
};

export default Footer;
